import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { alpha } from '@mui/material/styles';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

import { appURL } from '../Data/baseURL';
import { ContDePlataLogo_onwhite1 } from '../Data/imageURL';
import {
  GlassCard,
  PublicContainer,
  PublicPage,
  PublicPageHero,
  publicPalette,
} from '../Public/PublicLayout';

const NotFound = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useSelector((state) => state.authSlice);
  const destination = isAuthenticated ? appURL : '/';

  return (
    <PublicPage>
      <Helmet>
        <title>CONTDEPLATA — 404</title>
        <meta name="description" content={t('public.not_found_text')} />
        <meta property="og:title" content="CONTDEPLATA — 404" />
        <meta property="og:image" content={ContDePlataLogo_onwhite1} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image:alt" content="CONTDEPLATA" />
        <meta name="errorpage" content="true" />
        <meta name="errortype" content="404 - Not Found" />
        <meta name="prerender-status-code" content="404" />
      </Helmet>

      <PublicPageHero
        compact
        eyebrow="404"
        title={t('general.page_not_found')}
        description={t('public.not_found_text')}
      />

      <Box sx={{ pb: { xs: 8, md: 12 }, backgroundColor: publicPalette.background }}>
        <PublicContainer>
          <GlassCard sx={{ maxWidth: 680, mx: 'auto', p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: 76,
                height: 76,
                display: 'grid',
                placeItems: 'center',
                mx: 'auto',
                mb: 2.5,
                color: publicPalette.primary,
                border: `1px solid ${alpha(publicPalette.primary, 0.35)}`,
                borderRadius: 4,
                backgroundColor: alpha(publicPalette.primary, 0.09),
              }}
            >
              <ErrorOutlineRoundedIcon sx={{ fontSize: 38 }} />
            </Box>

            <Typography sx={{ mb: 3, color: publicPalette.muted, lineHeight: 1.75 }}>
              {t('public.not_found_hint')}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button
                component={NavLink}
                to={destination}
                startIcon={isAuthenticated ? <DashboardRoundedIcon /> : <ArrowBackRoundedIcon />}
                sx={{
                  px: 3,
                  py: 1.25,
                  color: '#03111A',
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 900,
                  background: `linear-gradient(110deg, ${publicPalette.primary}, ${publicPalette.accent})`,
                }}
              >
                {isAuthenticated ? t('button.back_to_dashboard') : t('button.back_to_main_page')}
              </Button>
            </Stack>
          </GlassCard>
        </PublicContainer>
      </Box>
    </PublicPage>
  );
};

export default NotFound;
